import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import AftercareRenderer from "@/components/AftercareRenderer";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

export default function Aftercare() {
  const { token } = useParams();
  const [state, setState] = useState({ status: "loading", data: null, error: null });
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    // noindex for the portal so search engines never crawl these tokenised
    // URLs, even if a client accidentally shares one.
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow, noarchive";
    document.head.appendChild(m);
    const prevTitle = document.title;
    document.title = "Your CLAÈ Aftercare";
    return () => { document.head.removeChild(m); document.title = prevTitle; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/aftercare/${encodeURIComponent(token)}`);
        if (cancelled) return;
        setState({ status: "ok", data: r.data, error: null });
        setActiveId(r.data?.treatments?.[0]?.id || null);
      } catch (e) {
        if (cancelled) return;
        const code = e?.response?.status;
        setState({
          status: "error",
          data: null,
          error: code === 404
            ? "This aftercare link is not valid or has expired."
            : "Something went wrong loading your aftercare. Please try again shortly.",
        });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const active = state.data?.treatments?.find((t) => t.id === activeId) || state.data?.treatments?.[0];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FAF9F6", color: "#141414" }} data-testid="aftercare-page">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <header className="text-center mb-10">
          <h1
            className="font-light tracking-[0.06em] text-[42px] sm:text-[54px]"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            data-testid="aftercare-brand"
          >
            CLAÈ
          </h1>
          <div className="mx-auto mt-3 h-[2px] w-12" style={{ backgroundColor: "#C5A059" }} />
          <p className="mt-6 text-[11px] tracking-[0.28em] uppercase text-[#66615C]" data-testid="aftercare-eyebrow">
            Your Aftercare
          </p>
          {state.data?.client_first_name && (
            <p className="mt-4 text-[15px] text-[#3F3B33]" data-testid="aftercare-greeting">
              Hi {state.data.client_first_name} — your personalised aftercare information for your recent treatment.
            </p>
          )}
        </header>

        {state.status === "loading" && (
          <div className="text-center text-[13px] text-[#66615C]" data-testid="aftercare-loading">
            Loading your aftercare…
          </div>
        )}

        {state.status === "error" && (
          <div
            className="border border-[#E5E1D8] bg-white p-8 text-center"
            data-testid="aftercare-error"
          >
            <p className="text-[13px] tracking-[0.24em] uppercase text-[#66615C] mb-3">Link unavailable</p>
            <p className="text-[14px] text-[#141414]">{state.error}</p>
            <p className="mt-4 text-[12px] text-[#66615C]">
              Please contact CLAÈ if you believe this is an error.
            </p>
          </div>
        )}

        {state.status === "ok" && state.data?.treatments?.length > 0 && (
          <div>
            {/* Treatment tabs — one per treatment received on this appointment */}
            <div
              className="flex flex-wrap gap-2 border-b border-[#E5E1D8] pb-0 mb-8"
              role="tablist"
              data-testid="aftercare-tablist"
            >
              {state.data.treatments.map((t) => {
                const isActive = t.id === (active?.id);
                return (
                  <button
                    key={t.id}
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActiveId(t.id)}
                    data-testid={`aftercare-tab-${t.id}`}
                    className="px-4 py-3 text-[11px] tracking-[0.22em] uppercase transition-colors"
                    style={{
                      color: isActive ? "#141414" : "#66615C",
                      borderBottom: isActive ? "2px solid #141414" : "2px solid transparent",
                      marginBottom: "-1px",
                      fontFamily: "'Plus Jakarta Sans', Arial, sans-serif",
                    }}
                  >
                    {t.name}
                  </button>
                );
              })}
            </div>

            {active && (
              <section
                data-testid={`aftercare-panel-${active.id}`}
                role="tabpanel"
                className="bg-white border border-[#E5E1D8] p-7 sm:p-10"
              >
                {active.category && (
                  <p className="text-[10px] tracking-[0.28em] uppercase text-[#66615C] mb-2">
                    {active.category}
                  </p>
                )}
                <h2
                  className="font-normal text-[26px] sm:text-[30px] leading-tight mb-6 text-[#141414]"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                  data-testid="aftercare-heading"
                >
                  {active.name} Aftercare
                </h2>
                <AftercareRenderer text={active.aftercare} />
              </section>
            )}

            <p className="mt-10 text-center text-[12px] text-[#66615C] leading-[1.7]">
              If you have any questions, or notice anything unusual after your treatment,
              please contact CLAÈ directly. We're here for you.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
